import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import SupplierPaymentModal from "../../core/modals/finance/SupplierPaymentModal";
import { fetchSupplierPayments } from "../../core/redux/slices/financeSlice";
import { fetchSuppliers } from "../../core/redux/slices/supplierSlice";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const SupplierPayments = () => {
  const dispatch = useDispatch();
  const { payments, loading } = useSelector((s) => s.finance);
  const suppliers = useSelector((s) => s.suppliers.list);
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchSupplierPayments());
  }, [dispatch]);

  const handleSearch = () => dispatch(fetchSupplierPayments());

  const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.supplierName }));

  // The /api/SupplierPayment/billwise/all endpoint accepts no query params — date filtering is applied client-side.
  const data = useMemo(() => {
    const q = search.toLowerCase();
    return payments.filter((p) => {
      const matchSearch = !q || p.supplierName?.toLowerCase().includes(q) || p.entryNo?.toLowerCase().includes(q);
      const matchSupplier = !supplierFilter || p.supplierID === supplierFilter;
      const d = p.paymentDate ? p.paymentDate.split("T")[0] : null;
      const matchDate = (!dateFrom || !d || d >= dateFrom) && (!dateTo || !d || d <= dateTo);
      return matchSearch && matchSupplier && matchDate;
    });
  }, [payments, search, supplierFilter, dateFrom, dateTo]);

  const closeModal = () => {
    setShowModal(false);
    dispatch(fetchSupplierPayments());
  };

  const columns = [
    { title: "Date", dataIndex: "paymentDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—", sorter: (a, b) => (a.paymentDate || "").localeCompare(b.paymentDate || "") },
    { title: "Supplier", dataIndex: "supplierName", sorter: (a, b) => (a.supplierName || "").localeCompare(b.supplierName || "") },
    { title: "Payment Mode", dataIndex: "paymentType", width: 120 },
    { title: "Reference", dataIndex: "entryNo", width: 150 },
    {
      title: "Amount Paid", dataIndex: "totalPaidAmount", align: "right", width: 130,
      render: (v) => <span className="text-success fw-semibold">{fmt(v)}</span>,
      sorter: (a, b) => (a.totalPaidAmount || 0) - (b.totalPaidAmount || 0),
    },
    {
      title: "Bills Settled", dataIndex: "details", align: "center", width: 110,
      render: (v) => (v || []).length || "—",
    },
    { title: "Remarks", dataIndex: "remarks", ellipsis: true },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Supplier Bill Payments" subtitle="Record bill-wise payments to suppliers" onAdd={() => setShowModal(true)} addLabel="New Payment" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} style={{ width: 140 }} />
              </div>
              <button className="btn btn-sm btn-primary" onClick={handleSearch}>Search</button>
              <div style={{ minWidth: 180 }}>
                <Select classNamePrefix="react-select" options={supplierOptions}
                  value={supplierOptions.find((o) => o.value === supplierFilter) || null}
                  placeholder="All Suppliers"
                  isClearable
                  onChange={(opt) => setSupplierFilter(opt?.value || "")} />
              </div>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search supplier or reference..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <SupplierPaymentModal show={showModal} onClose={closeModal} />
    </div>
  );
};

export default SupplierPayments;
